/**
 * iter-189-fix-2 — Date grouping helper used by Movements, Payments
 * and Receivables to render their lists collapsed by day / week /
 * month with a sticky header per group.
 *
 * Extracted from /app/frontend/src/pages/dashboard/History.jsx so
 * Pagos and Cobros can adopt the SAME UX byte-identically (the user
 * explicitly asked for this in iter-189-fix-2).
 *
 * Usage
 * -----
 * ```jsx
 * const groupBy = useState("day")
 * const groups = useGroupedByDate(items, groupBy, (it) => it.due_date)
 * {groups.map(({ key, label, items }) => …)}
 * ```
 */
import { useMemo } from "react";

const D = (iso) => {
  if (!iso) return new Date();
  return new Date(`${iso.slice(0, 10)}T12:00:00Z`);
};

export const localDateKey = (iso) => (iso ? iso.slice(0, 10) : "");

export const weekKey = (iso) => {
  if (!iso) return "";
  const d = D(iso);
  const [y, w] = d.toISOString().slice(0, 10).split("-");
  // ISO week-of-year via Date.getUTCDay + offset
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((target.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  void y; void w;
  return `${target.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
};

export const monthKey = (iso) => (iso ? iso.slice(0, 7) : "");

/**
 * Group ``items`` by date bucket. ``dateAccessor`` extracts the ISO
 * date string from each item (defaults to ``item.date``). Returns an
 * ordered array of ``{key, label, items}`` sorted by ``order`` —
 * ``"desc"`` (default, newest first, matches Movements) or ``"asc"``
 * (oldest/earliest first, used by Receivables and Payments so the
 * closest-due item appears at the top — iter-211.2).
 *
 * iter-213.7 — additional groupBy value: ``"tag"`` groups by the
 * primary tag of each item (the first entry of ``item.tags`` array,
 * fall-back to ``item.tag``). Items without any tag fall into the
 * ``"_untagged"`` bucket rendered at the very bottom regardless of
 * ``order``. Within each tag bucket, items are still sorted by date
 * (newest/oldest first per the same ``order`` flag).
 */
export function useGroupedByDate(items, groupBy, dateAccessor = (i) => i.date, lang = "es", order = "desc") {
  return useMemo(() => {
    if (!items || items.length === 0) return [];
    const isTagGroup = groupBy === "tag";
    const tagKeyFn = (it) => {
      const t = (it.tags && it.tags.length > 0)
        ? it.tags[0]
        : (typeof it.tag === "string" ? it.tag : "");
      return (t || "").trim() || "_untagged";
    };
    const dateKeyFn = groupBy === "month" ? monthKey : groupBy === "week" ? weekKey : localDateKey;
    const keyFn = isTagGroup ? tagKeyFn : (it) => dateKeyFn(dateAccessor(it) || "");
    const buckets = {};
    for (const it of items) {
      const k = keyFn(it);
      if (!buckets[k]) buckets[k] = [];
      buckets[k].push(it);
    }
    // iter-211.2 — Stable item ordering inside each bucket so the list
    // is fully sorted, not just bucketed. Tag groups still sort their
    // contents by date for predictable scanning.
    const itemDir = order === "asc" ? 1 : -1;
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a, b) => {
        const da = dateAccessor(a) || "";
        const db = dateAccessor(b) || "";
        return da < db ? -itemDir : da > db ? itemDir : 0;
      });
    }
    const locale = lang === "en" ? "en-US" : lang === "pt" ? "pt-BR" : "es-ES";
    const untaggedLabel = lang === "en" ? "Without tag" : lang === "pt" ? "Sem etiqueta" : "Sin etiqueta";
    const labelFor = (k) => {
      if (!k) return "—";
      if (isTagGroup) return k === "_untagged" ? untaggedLabel : k;
      if (groupBy === "day") {
        const d = D(k);
        return d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      }
      if (groupBy === "week") return k;
      // month
      const d = D(`${k}-01`);
      return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
    };
    const groupDir = order === "asc" ? 1 : -1;
    const sortKeys = (a, b) => {
      if (isTagGroup) {
        // Untagged always last (regardless of order direction).
        if (a === "_untagged") return 1;
        if (b === "_untagged") return -1;
        // Other tags: sort by item count DESC (heaviest first) so the
        // user's most-used tags surface at the top.
        const ca = buckets[a].length;
        const cb = buckets[b].length;
        if (ca !== cb) return cb - ca;
        return a.localeCompare(b, locale);
      }
      return a < b ? -groupDir : a > b ? groupDir : 0;
    };
    return Object.keys(buckets)
      .sort(sortKeys)
      .map((k) => ({ key: k, label: labelFor(k), items: buckets[k] }));
  }, [items, groupBy, dateAccessor, lang, order]);
}
